import { useState } from 'react';
import round from '../../../helpers/round';
import { Alert, Typography, Box, IconButton, Collapse, Tooltip, useMediaQuery } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from '@mui/material/Link';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';

const HansenResultBaseFactors = ({ result }) => {
  const isSmallScreen = useMediaQuery('(max-width:700px)');
  const { cohesion, bearingCapacityFactorOfSafety } = result.input;

  const {
    canUserSeeCalculationSteps,
    effectiveFndWidth,
    effectiveFndLength,
    isInclinedLoading,
    isPhiZero,
    //bearing capacity factors
    nc,
    nq,
    nGamma,
    //depth factors
    dc,
    dcb,
    dcl,
    dq,
    dqb,
    dql,
    dGamma,
    dGammab,
    dGammal,
    //inclination factors
    ic,
    icb,
    icl,
    iq,
    iqb,
    iql,
    iGamma,
    iGammab,
    iGammal,
    //shape factors
    sc,
    scb,
    scl,
    sq,
    sqb,
    sql,
    sGammab,
    sGammal,
    sGamma,
    //ground factors
    gc,
    gq,
    gGamma,
    //base factors
    bc,
    bq,
    bGamma,
    // water table related
    effectiveStress,
    gammaEffective,
    //q ult
    term1,
    term2,
    term3,
    qult,
    qall,
    EQ1_inclinedTerm1,
    EQ1_inclinedTerm2,
    EQ1_inclinedTerm3,
    EQ1_inclinedQult,
    //Equation 2 terms
    EQ2_inclinedTerm1,
    EQ2_inclinedTerm2,
    EQ2_inclinedTerm3,
    EQ2_inclinedQult,
    qultInclined,
    qallInclined,
  } = result.output;

  const case1 = isInclinedLoading && isPhiZero;
  const case2 = isInclinedLoading && !isPhiZero;
  const case3 = !isInclinedLoading && isPhiZero;
  const case4 = !isInclinedLoading && !isPhiZero;
  const caseLegendItems = [
    { label: 'CASE 1', description: 'Inclined loading with \u03c6 = 0', isActive: case1 },
    { label: 'CASE 2', description: 'Inclined loading with \u03c6 \u2260 0', isActive: case2 },
    { label: 'CASE 3', description: 'No inclined loading with \u03c6 = 0', isActive: case3 },
    { label: 'CASE 4', description: 'No inclined loading with \u03c6 \u2260 0', isActive: case4 },
  ];

  const [showCase1Details, setShowCase1Details] = useState(false);
  const [showCase2Details, setShowCase2Details] = useState(false);
  const [showCase3Details, setShowCase3Details] = useState(false);
  const [showCase4Details, setShowCase4Details] = useState(false);

  const renderCaseBadge = (label, sx = {}) => (
    <Box
      component="span"
      sx={{
        ml: 1,
        px: 1,
        py: 0.2,
        borderRadius: 999,
        border: '1px solid',
        borderColor: 'success.light',
        color: 'success.dark',
        backgroundColor: 'rgba(46, 125, 50, 0.12)',
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: 0.3,
        lineHeight: 1.2,
        display: 'inline-flex',
        alignItems: 'center',
        ...sx,
      }}
    >
      {label}
    </Box>
  );

  const successAlertSx = {
    backgroundColor: 'rgba(46,125,50,0.04)',
    alignItems: 'flex-start',
    '& .MuiAlert-icon': {
      alignSelf: 'flex-start',
      mt: 0.25,
      minWidth: 24,
    },
    '& .MuiAlert-action': {
      alignSelf: 'flex-start',
      mt: 0.25,
      width: 32,
      justifyContent: 'center',
      p: 0,
      ml: 1,
    },
    '& .MuiAlert-message': { width: '100%' },
  };

  const renderAlertAction = (isExpanded, setExpanded) =>
    canUserSeeCalculationSteps ? (
      <IconButton size="small" color="inherit" onClick={() => setExpanded((v) => !v)} sx={{ width: 32, height: 32 }}>
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
          }}
        />
      </IconButton>
    ) : (
      <Tooltip
        title={
          <span>
            Upgrade to unlock calculation details.{' '}
            <Link href="/pricing" target="_blank" rel="noopener" style={{ color: '#90caf9', textDecoration: 'underline' }}>
              See plans
            </Link>
          </span>
        }
        leaveDelay={500}
        arrow
      >
        <LockOutlinedIcon
          sx={{
            fontSize: 20,
            color: 'text.disabled',
            cursor: 'not-allowed',
            mr: 1,
            mt: 0.5,
          }}
        />
      </Tooltip>
    );

  const Case1BearingCapacityDetails = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
        If H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading) and &phi; = 0
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &darr;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        calculate q<sub>ult</sub> using the equation below.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult</sub> = 5.14 s<sub>u</sub> (1 + s'<sub>c</sub> + d'<sub>c</sub> - i'<sub>c</sub> - b'<sub>c</sub> - g'
        <sub>c</sub>) + q&#772;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Using the previously calculated values we have:
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult</sub> = (5.14) ({round(cohesion, 2)}) [1 + ({round(sc, 2)}) + ({round(dc, 2)}) - ({round(ic, 2)}) - (
        {round(bc, 2)}) - ({round(gc, 2)})] + {round(effectiveStress, 2)}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult</sub> = {round(qult, 0)} kPa
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>all</sub> = q<sub>ult</sub> / FS<sub>Bearing</sub>
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>all</sub> = {round(qult, 0)} / {round(bearingCapacityFactorOfSafety, 2)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        q<sub>all</sub> = {round(qall, 0)} kPa
      </Typography>
    </>
  );

  const Case2BearingCapacityDetails = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
        If H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading) and &phi; &gt; 0
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &darr;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Calculate q<sub>ult</sub> using both of the equations below and use the smaller value.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Using the previously calculated values we have:
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Equation - 1
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult-1</sub> = (c N<sub>c</sub> s<sub>c, B</sub> d<sub>c, B</sub> i<sub>c, B</sub> b<sub>c</sub> g<sub>c</sub>) +
        (q&#772; N<sub>q</sub> s<sub>q, B</sub> d<sub>q, B</sub> i<sub>q, B</sub> b<sub>q</sub> g<sub>q</sub>) + (0.5 &gamma;
        <sub>e</sub> B' N<sub>&gamma;</sub> s<sub>&gamma;, B</sub> d<sub>&gamma;, B</sub> i<sub>&gamma;, B</sub> b
        <sub>&gamma;</sub> g<sub>&gamma;</sub>)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult-1</sub> = ({round(cohesion, 2)}) ({round(nc, 2)}) ({round(scb, 2)}) ({round(dcb, 2)}) ({round(icb, 2)}) (
        {round(bc, 2)}) ({round(gc, 2)}) + ({round(effectiveStress, 2)}) ({round(nq, 2)}) ({round(sqb, 2)}) ({round(dqb, 2)}) (
        {round(iqb, 2)}) ({round(bq, 2)}) ({round(gq, 2)}) + (0.5) ({round(gammaEffective, 2)}) ({round(effectiveFndWidth, 2)}) (
        {round(nGamma, 2)}) ({round(sGammab, 2)}) ({round(dGammab, 2)}) ({round(iGammab, 2)}) ({round(bGamma, 2)}) (
        {round(gGamma, 2)})
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult-1</sub> = ({round(EQ1_inclinedTerm1, 0)}) + ({round(EQ1_inclinedTerm2, 0)}) + ({round(EQ1_inclinedTerm3, 0)})
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult-1</sub> = {round(EQ1_inclinedQult, 0)} kPa
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Equation - 2
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult-2</sub> = (c N<sub>c</sub> s<sub>c, L</sub> d<sub>c, L</sub> i<sub>c, L</sub> b<sub>c</sub> g<sub>c</sub>) +
        (q&#772; N<sub>q</sub> s<sub>q, L</sub> d<sub>q, L</sub> i<sub>q, L</sub> b<sub>q</sub> g<sub>q</sub>) + (0.5 &gamma;
        <sub>e</sub> L' N<sub>&gamma;</sub> s<sub>&gamma;, L</sub> d<sub>&gamma;, L</sub> i<sub>&gamma;, L</sub> b
        <sub>&gamma;</sub> g<sub>&gamma;</sub>)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult-2</sub> = ({round(cohesion, 2)}) ({round(nc, 2)}) ({round(scl, 2)}) ({round(dcl, 2)}) ({round(icl, 2)}) (
        {round(bc, 2)}) ({round(gc, 2)}) + ({round(effectiveStress, 2)}) ({round(nq, 2)}) ({round(sql, 2)}) ({round(dql, 2)}) (
        {round(iql, 2)}) ({round(bq, 2)}) ({round(gq, 2)}) + (0.5) ({round(gammaEffective, 2)}) ({round(effectiveFndLength, 2)}) (
        {round(nGamma, 2)}) ({round(sGammal, 2)}) ({round(dGammal, 2)}) ({round(iGammal, 2)}) ({round(bGamma, 2)}) (
        {round(gGamma, 2)})
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult-2</sub> = ({round(EQ2_inclinedTerm1, 0)}) + ({round(EQ2_inclinedTerm2, 0)}) + ({round(EQ2_inclinedTerm3, 0)})
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult-2</sub> = {round(EQ2_inclinedQult, 0)} kPa
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult</sub> = Min (q<sub>ult-1</sub>, q<sub>ult-2</sub>)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult</sub> = Min ({round(EQ1_inclinedQult, 0)}, {round(EQ2_inclinedQult, 0)})
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult</sub> = {round(qultInclined, 0)} kPa
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>all</sub> = q<sub>ult</sub> / FS<sub>Bearing</sub>
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>all</sub> = {round(qultInclined, 0)} / {round(bearingCapacityFactorOfSafety, 2)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        q<sub>all</sub> = {round(qallInclined, 0)} kPa
      </Typography>
    </>
  );

  const Case3BearingCapacityDetails = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
        If H<sub>B</sub> = 0 &amp; H<sub>L</sub> = 0 (No Inclined loading) and &phi; = 0
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &darr;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        calculate q<sub>ult</sub> using the equation below.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult</sub> = 5.14 s<sub>u</sub> (1 + s'<sub>c</sub> + d'<sub>c</sub> - i'<sub>c</sub> - b'<sub>c</sub> - g'
        <sub>c</sub>) + q&#772;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Using the previously calculated values we have:
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult</sub> = (5.14) ({round(cohesion, 2)}) [1 + ({round(sc, 2)}) + ({round(dc, 2)}) - ({round(ic, 2)}) - (
        {round(bc, 2)}) - ({round(gc, 2)})] + {round(effectiveStress, 2)}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult</sub> = {round(qult, 0)} kPa
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>all</sub> = q<sub>ult</sub> / FS<sub>Bearing</sub>
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>all</sub> = {round(qult, 0)} / {round(bearingCapacityFactorOfSafety, 2)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        q<sub>all</sub> = {round(qall, 0)} kPa
      </Typography>
    </>
  );

  const Case4BearingCapacityDetails = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
        If H<sub>B</sub> = 0 &amp; H<sub>L</sub> = 0 (No Inclined loading) and &phi; &gt; 0
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &darr;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        calculate q<sub>ult</sub> using the equation below.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult</sub> = (c N<sub>c</sub> s<sub>c</sub> d<sub>c</sub> i<sub>c</sub> b<sub>c</sub> g<sub>c</sub>) + (q&#772; N
        <sub>q</sub> s<sub>q</sub> d<sub>q</sub> i<sub>q</sub> b<sub>q</sub> g<sub>q</sub>) + (0.5 &gamma;<sub>e</sub> B' N
        <sub>&gamma;</sub> s<sub>&gamma;</sub> d<sub>&gamma;</sub> i<sub>&gamma;</sub> b<sub>&gamma;</sub> g<sub>&gamma;</sub>)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Using the previously calculated values we have:
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult</sub> = ({round(cohesion, 2)}) ({round(nc, 2)}) ({round(sc, 2)}) ({round(dc, 2)}) ({round(ic, 2)}) (
        {round(bc, 2)}) ({round(gc, 2)}) + ({round(effectiveStress, 2)}) ({round(nq, 2)}) ({round(sq, 2)}) ({round(dq, 2)}) (
        {round(iq, 2)}) ({round(bq, 2)}) ({round(gq, 2)}) + (0.5) ({round(gammaEffective, 2)}) ({round(effectiveFndWidth, 2)}) (
        {round(nGamma, 2)}) ({round(sGamma, 2)}) ({round(dGamma, 2)}) ({round(iGamma, 2)}) ({round(bGamma, 2)}) (
        {round(gGamma, 2)})
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult</sub> = ({round(term1, 0)}) + ({round(term2, 0)}) + ({round(term3, 0)})
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>ult</sub> = {round(qult, 0)} kPa
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>all</sub> = q<sub>ult</sub> / FS<sub>Bearing</sub>
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        q<sub>all</sub> = {round(qult, 0)} / {round(bearingCapacityFactorOfSafety, 2)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        q<sub>all</sub> = {round(qall, 0)} kPa
      </Typography>
    </>
  );

  return (
    <>
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Ultimate and Allowable Bearing Capacity
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
            This section computes ultimate bearing capacity (q<sub>ult</sub>) and allowable bearing capacity (q<sub>all</sub>)
            based on load inclination and friction-angle conditions. The active case controls whether a single equation or
            two-direction inclined equations are used.
          </Typography>
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              border: '1px solid',
              borderColor: 'info.light',
              borderRadius: 2,
            }}
          >
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={700}>
                Case Legend
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.8 }}>
              {caseLegendItems.map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    border: '1px solid',
                    borderColor: item.isActive ? 'success.main' : 'divider',
                    borderRadius: 1.5,
                    px: 1,
                    py: 0.7,
                    backgroundColor: item.isActive ? 'rgba(46,125,50,0.08)' : 'rgba(255,255,255,0.45)',
                  }}
                >
                  {renderCaseBadge(item.label, {
                    ml: 0,
                    minWidth: 64,
                    justifyContent: 'center',
                    borderColor: item.isActive ? 'success.main' : 'divider',
                    color: item.isActive ? 'success.dark' : 'text.disabled',
                    backgroundColor: item.isActive ? 'rgba(46, 125, 50, 0.16)' : 'rgba(0, 0, 0, 0.03)',
                    fontWeight: item.isActive ? 700 : 600,
                  })}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '0.82rem', fontWeight: item.isActive ? 600 : 500 }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* //? CASE 1 - INCLINED LOADING && PHI = 0 */}
            {case1 && (
              <Alert
                icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
                severity="success"
                variant="outlined"
                sx={successAlertSx}
                action={renderAlertAction(showCase1Details, setShowCase1Details)}
              >
                <Typography variant="body2" color="text.secondary" component="div">
                  <b>
                    q<sub>ult</sub> = {round(qult, 0)} kPa, q<sub>all</sub> = {round(qall, 0)} kPa
                  </b>
                </Typography>
                {canUserSeeCalculationSteps && (
                  <Collapse in={showCase1Details} unmountOnExit>
                    <Case1BearingCapacityDetails />
                  </Collapse>
                )}
              </Alert>
            )}

            {/* //? CASE 2 - INCLINED LOADING && PHI > 0 */}
            {case2 && (
              <Alert
                icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
                severity="success"
                variant="outlined"
                sx={successAlertSx}
                action={renderAlertAction(showCase2Details, setShowCase2Details)}
              >
                <Typography variant="body2" color="text.secondary" component="div">
                  <b>
                    q<sub>ult</sub> = {round(qultInclined, 0)} kPa, q<sub>all</sub> = {round(qallInclined, 0)} kPa
                  </b>
                </Typography>
                {canUserSeeCalculationSteps && (
                  <Collapse in={showCase2Details} unmountOnExit>
                    <Case2BearingCapacityDetails />
                  </Collapse>
                )}
              </Alert>
            )}

            {/* //? CASE 3 - NOT INCLINED LOADING = 0 && PHI = 0 */}
            {case3 && (
              <Alert
                icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
                severity="success"
                variant="outlined"
                sx={successAlertSx}
                action={renderAlertAction(showCase3Details, setShowCase3Details)}
              >
                <Typography variant="body2" color="text.secondary" component="div">
                  <b>
                    q<sub>ult</sub> = {round(qult, 0)} kPa, q<sub>all</sub> = {round(qall, 0)} kPa
                  </b>
                </Typography>
                {canUserSeeCalculationSteps && (
                  <Collapse in={showCase3Details} unmountOnExit>
                    <Case3BearingCapacityDetails />
                  </Collapse>
                )}
              </Alert>
            )}

            {/* //? CASE 4 - NOT INCLINED LOADING = 0 && PHI > 0 */}
            {case4 && (
              <Alert
                icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
                severity="success"
                variant="outlined"
                sx={successAlertSx}
                action={renderAlertAction(showCase4Details, setShowCase4Details)}
              >
                <Typography variant="body2" color="text.secondary" component="div">
                  <b>
                    q<sub>ult</sub> = {round(qult, 0)} kPa, q<sub>all</sub> = {round(qall, 0)} kPa
                  </b>
                </Typography>
                {canUserSeeCalculationSteps && (
                  <Collapse in={showCase4Details} unmountOnExit>
                    <Case4BearingCapacityDetails />
                  </Collapse>
                )}
              </Alert>
            )}
          </Box>
        </Alert>
      </Box>
    </>
  );
};
export default HansenResultBaseFactors;
